/* eslint-disable react-hooks/exhaustive-deps, react/prop-types */
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { jwtDecode } from "jwt-decode";
import { useNavigate, Link, useParams } from 'react-router-dom';
import Pagination from "react-js-pagination";
import Navbar from "../Navbar";
import Header from "../Header";
import baseUrl from "../BaseUrl";
import { formatUTCDate } from "../../utils/utils";
import { formatDurationMs } from "../../utils/formatDurationMs";
import ExportButton from "../active-users/ExportButton";
import DateRangeBar, { fmt, presetRange } from "../active-users/DateRangeBar";
import EmptyState from "../active-users/states/EmptyState";
import ErrorState from "../active-users/states/ErrorState";
import TableSkeleton from "../active-users/states/TableSkeleton";
import StandingCheckDetailSummaryCards from "./StandingCheckDetailSummaryCards";
import "../active-users/active-users.css";
import "./StandingCheck.css";

const LIMIT = 20;

// '1' → Yes, anything else → No (matches on-screen columns + the export contract §7).
const yesNo = (v) => (v === '1' ? 'Yes' : 'No');

const EXPORT_COLUMNS = [
  { key: 'peak_hr', label: 'Peak HR' },
  { key: 'baseline_hr', label: 'Baseline HR' },
  { key: 'time_to_peak', label: 'Time to Peak' },
  // Raw milliseconds in the CSV (spreadsheet-friendly); the table shows them as "1m 30s".
  { key: 'selected_sit_time', label: 'Selected Sit Time (ms)' },
  { key: 'selected_stand_time', label: 'Selected Stand Time (ms)' },
  { key: 'test_aborted', label: 'Test Aborted' },
  { key: 'platform', label: 'Platform' },
  { key: 'timestamp_utc', label: 'Timestamp UTC' },
  { key: 'extra_detail', label: 'Extra Detail' },
  { key: 'in_doctor_report', label: 'In Doctor Report' },
  { key: 'stand_time_stamp', label: 'Stand Timestamp' },
  { key: 'peak_time_stamp', label: 'Peak Timestamp' },
  { key: 'sit_start_time_stamp', label: 'Sit Start Timestamp' },
];

const StandingCheckDetailList = () => {
  const navigate = useNavigate();
  const { encodedUserId } = useParams();
  const decodedUserId = encodedUserId ? atob(encodedUserId) : '';

  const [token, setToken] = useState('');
  const [expire, setExpire] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [records, setData] = useState([]);
  const [totalItem, setTotalItem] = useState(0);
  const [page, setPage] = useState(1);

  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [displayFromDate, setDisplayFromDate] = useState(null);
  const [displayToDate, setDisplayToDate] = useState(null);
  const [preset, setPreset] = useState(null);
  const [sortField, setSortField] = useState('timestamp_utc');
  const [sortOrder, setSortOrder] = useState('DESC');

  const [summaryCards, setSummaryCards] = useState({
    user: null, total_records: 0, aborted_count: 0, in_doctor_report_count: 0, avg_peak_hr: null, avg_baseline_hr: null,
  });

  const axiosJWT = axios.create();
  axiosJWT.interceptors.request.use(async (config) => {
    if (expire * 1000 < new Date().getTime()) {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      config.headers.Authorization = `Bearer ${res.data.accessToken}`;
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    }
    return config;
  }, (err) => Promise.reject(err));

  useEffect(() => {
    // No default date window — the detail page opens all-time; the user can
    // narrow with the date range bar.
    refreshToken();
  }, []);

  const refreshToken = async () => {
    try {
      const res = await axios.get(`${baseUrl}/admin/api/token`);
      setToken(res.data.accessToken);
      setExpire(jwtDecode(res.data.accessToken).exp);
    } catch (err) {
      if (err.response) { navigate('/'); window.location.reload(); }
    }
  };

  useEffect(() => {
    if (token) loadAll(page, fromDate, toDate, sortField, sortOrder);
  }, [token]);

  const auth = () => ({ headers: { Authorization: `Bearer ${token}` } });
  const listQs = (p, f, t, sf, so) => {
    const qp = new URLSearchParams({
      page: p, limit: LIMIT, from_date: f, to_date: t,
      ...(decodedUserId && { user_id: decodedUserId }),
      ...(sf && { sortField: sf }), ...(so && { sortOrder: so }),
    });
    return qp.toString();
  };
  const summaryQs = (f, t) => `user_id=${decodedUserId}&from_date=${f}&to_date=${t}`;
  const handleErr = (err) => { setError(err?.response?.status === 401 ? 'auth' : 'load'); };

  const getList = async (p, f, t, sf, so) => {
    setLoading(true); setError('');
    try {
      const res = await axiosJWT.get(`${baseUrl}/admin/api/standing-check-details?${listQs(p, f, t, sf, so)}`, auth());
      setData(res.data.data || []);
      setTotalItem(res.data.totalItem || 0);
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const loadAll = async (p, f, t, sf, so) => {
    setLoading(true); setError('');
    try {
      const [list, sum] = await Promise.all([
        axiosJWT.get(`${baseUrl}/admin/api/standing-check-details?${listQs(p, f, t, sf, so)}`, auth()),
        axiosJWT.get(`${baseUrl}/admin/api/standing-check-detail-summary?${summaryQs(f, t)}`, auth()),
      ]);
      setData(list.data.data || []);
      setTotalItem(list.data.totalItem || 0);
      setSummaryCards(sum.data.data || {
        user: null, total_records: 0, aborted_count: 0, in_doctor_report_count: 0, avg_peak_hr: null, avg_baseline_hr: null,
      });
    } catch (err) { handleErr(err); } finally { setLoading(false); }
  };

  const handlePageChange = (pageNumber) => {
    setPage(pageNumber);
    getList(pageNumber, fromDate, toDate, sortField, sortOrder);
  };
  const handleFromChange = (date) => { setPreset('custom'); setDisplayFromDate(date); setFromDate(date ? fmt(date) : ''); };
  const handleToChange = (date) => { setPreset('custom'); setDisplayToDate(date); setToDate(date ? fmt(date) : ''); };
  const handlePreset = (key, fromStr, toStr) => {
    const { fromStr: fs, toStr: ts } = presetRange(key, new Date());
    setPreset(key); setFromDate(fromStr || fs); setToDate(toStr || ts);
    setDisplayFromDate(new Date(fromStr || fs)); setDisplayToDate(new Date(toStr || ts));
    setPage(1);
    loadAll(1, fromStr || fs, toStr || ts, sortField, sortOrder);
  };
  const handleSearch = () => { setPage(1); loadAll(1, fromDate, toDate, sortField, sortOrder); };
  const clearFilter = () => {
    setFromDate(''); setToDate(''); setDisplayFromDate(null); setDisplayToDate(null);
    setPreset(null); setSortField('timestamp_utc'); setSortOrder('DESC'); setPage(1);
    loadAll(1, '', '', 'timestamp_utc', 'DESC');
  };
  const handleSort = (field) => {
    const order = sortField === field && sortOrder === 'ASC' ? 'DESC' : 'ASC';
    setSortField(field); setSortOrder(order); setPage(1);
    loadAll(1, fromDate, toDate, field, order);
  };

  const fetchExport = async () => {
    const res = await axiosJWT.get(
      `${baseUrl}/admin/api/standing-check-detail-export?user_id=${decodedUserId}&from_date=${fromDate}&to_date=${toDate}&sortField=${sortField}&sortOrder=${sortOrder}`,
      auth());
    return (res.data.data || []).map((r) => ({
      ...r,
      test_aborted: yesNo(r.test_aborted),
      in_doctor_report: yesNo(r.in_doctor_report),
      timestamp_utc: r.timestamp_utc ? formatUTCDate(r.timestamp_utc) : '',
    }));
  };

  const SortTh = ({ field, label }) => (
    <th className="au-th-sort" onClick={() => handleSort(field)}>
      {label} {sortField === field ? (sortOrder === 'ASC' ? '🔼' : '🔽') : ''}
    </th>
  );
  const hasRange = !!(fromDate || toDate);
  const dmy = (s) => { if (!s) return ''; const [y, m, d] = s.split('-'); return `${d}/${m}/${y}`; };
  const userName = summaryCards.user?.name || '';

  return (
    <div className="panelbox">
      <Navbar />
      <div className="right-panel">
        <Header name="Pauser" />
        <div className="content-page">
          <div className="au-head">
            <span className="au-breadcrumb">Standing check sessions › <b>Details</b></span>
            <span className="au-route">/standing-check-details</span>
            <span className="au-spacer" />
            <span className="au-back"><Link to="/standing-check-sessions">← Back</Link></span>
          </div>
          <h1 className="au-title">Standing Check Details{userName ? <span className="au-subtitle"> — {userName}</span> : null}</h1>

          {error === 'auth' ? (
            <ErrorState title="Your session expired" message="Please sign in again to continue." onRetry={() => navigate('/')} />
          ) : error === 'load' ? (
            <ErrorState message="We couldn’t load the standing check details. Please try again." onRetry={() => loadAll(page, fromDate, toDate, sortField, sortOrder)} />
          ) : (
            <>
              {/* Summary before detail */}
              <StandingCheckDetailSummaryCards
                totalRecords={summaryCards.total_records}
                abortedCount={summaryCards.aborted_count}
                inDoctorReportCount={summaryCards.in_doctor_report_count}
                avgPeakHr={summaryCards.avg_peak_hr}
                avgBaselineHr={summaryCards.avg_baseline_hr}
              />

              <DateRangeBar
                preset={preset}
                displayFrom={displayFromDate}
                displayTo={displayToDate}
                hasRange={hasRange}
                onPreset={handlePreset}
                onFromChange={handleFromChange}
                onToChange={handleToChange}
                onSearch={handleSearch}
                onClear={clearFilter}
              />
              <p className="au-range-note" data-testid="sc-detail-range-note">
                The date range filters standing checks by their <b>record date (UTC)</b> — the value shown as “Timestamp UTC”.{' '}
                {(fromDate || toDate)
                  ? <>Showing <b>{dmy(fromDate) || '…'}</b> – <b>{dmy(toDate) || '…'}</b>.</>
                  : 'Showing all standing checks (no date filter).'}
              </p>

              {/* Detail table */}
              {loading ? (
                <div className="au-panel"><TableSkeleton rows={8} /></div>
              ) : records.length === 0 ? (
                <EmptyState title="No standing checks in this range" hasFilters={hasRange} onClear={clearFilter}
                  message={hasRange ? 'This user has no standing check records for the current filters.' : 'This user has no standing check records to show yet.'} />
              ) : (
                <div className="au-panel">
                  <div className="au-panel__head">
                    <h4 className="au-panel__title">Standing check records</h4>
                    <span className="au-spacer" />
                    <ExportButton columns={EXPORT_COLUMNS} fetchRows={fetchExport} filename="standing-check-details.csv" />
                  </div>
                  <div className="au-table-wrap">
                    <table className="au-table sc-table" data-testid="sc-detail-table">
                      <thead>
                        <tr>
                          <th>No</th>
                          <th>Peak HR</th><th>Baseline HR</th><th>Time to Peak</th>
                          <th>Selected Sit Time</th><th>Selected Stand Time</th>
                          <th>Test Aborted</th><th>Platform</th>
                          <SortTh field="timestamp_utc" label="Timestamp UTC" />
                          <th>Extra Detail</th><th>In Doctor Report</th>
                          <th>Stand Timestamp</th><th>Peak Timestamp</th><th>Sit Start Timestamp</th>
                        </tr>
                      </thead>
                      <tbody>
                        {records.map((row, index) => (
                          <tr key={row.id ?? index}>
                            <td data-label="No">{(page - 1) * LIMIT + index + 1}</td>
                            <td data-label="Peak HR" className="tnum">{row.peak_hr || '-'}</td>
                            <td data-label="Baseline HR" className="tnum">{row.baseline_hr || '-'}</td>
                            <td data-label="Time to Peak" className="tnum">{row.time_to_peak || '-'}</td>
                            <td data-label="Selected Sit Time" className="tnum">{formatDurationMs(row.selected_sit_time)}</td>
                            <td data-label="Selected Stand Time" className="tnum">{formatDurationMs(row.selected_stand_time)}</td>
                            <td data-label="Test Aborted">{yesNo(row.test_aborted)}</td>
                            <td data-label="Platform">{row.platform || '-'}</td>
                            <td data-label="Timestamp UTC">{row.timestamp_utc ? formatUTCDate(row.timestamp_utc) : '-'}</td>
                            <td data-label="Extra Detail" className="au-ellipsis sc-extra">{row.extra_detail || '-'}</td>
                            <td data-label="In Doctor Report">{yesNo(row.in_doctor_report)}</td>
                            <td data-label="Stand Timestamp">{formatUTCDate(row.stand_time_stamp) || '-'}</td>
                            <td data-label="Peak Timestamp">{formatUTCDate(row.peak_time_stamp) || '-'}</td>
                            <td data-label="Sit Start Timestamp">{formatUTCDate(row.sit_start_time_stamp) || '-'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {totalItem > LIMIT && (
                    <Pagination activePage={page} itemsCountPerPage={LIMIT} totalItemsCount={totalItem} pageRangeDisplayed={5} onChange={handlePageChange} />
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StandingCheckDetailList;
